import React, {useState} from 'react';
import graphql from 'babel-plugin-relay/macro';
import {useRefetchable} from 'relay-hooks';
import {TourPreview_tour$key} from '../../../../__generated__/TourPreview_tour.graphql';
import {FrameStoreBuffer} from '../../types';
import {TourPlayer} from '../../components/TourPlayer';
import {TourPreviewMenu} from './TourPreviewMenu';
import {TourPreviewDrawer} from './TourPreviewDrawer';
import {
  TOUR_DRAWER_HEIGHT,
  TOUR_DRAWER_WIDTH,
} from '../../components/TourDrawer';
import {Stack, useMediaQuery} from '@mui/material';
import {Theme} from '@mui/material/styles';
import {useSearchParam} from 'react-use';
import {TourWebRTC} from './TourPreviewWebRTC';
import {TourUIContextProvider} from '../../contexts/TourUIContext';

const fragment = graphql`
  fragment TourPreview_tour on Tour
  @refetchable(queryName: "TourPreviewTodoRefetchQuery") {
    ...TourPreviewMarkers_tour
    ...TourPlayerCanvas_tour
    id
    thumbnailURL
    title
    viewingSessionRequest {
      rtcToken
      signalingToken
      appId
      viewingSessionUserId
      expireTime
    }
    inspections {
      id
      title
      inspectionStatus
      inspectionDate
      propertyAddress
      inspectorName
      defects {
        id
        title
        description
        severity
        defectStatus
        roomType
        defectCategory
        sourceClipIndex
        sourceFramePosition
        aiDetected
        aiConfidence
        evidenceImageURI
        audioStartSeconds
        audioEndSeconds
      }
      audioSubmissions {
        id
        videoURI
        originalFilename
        submissionStatus
        rawTranscript
        durationSeconds
        errorMessage
        createdAt
      }
    }
    clips {
      id
      index
      frameCount
    }
  }
`;
interface TourPreviewProps {
  tourRef: TourPreview_tour$key;
  external?: boolean;
}

export function TourPreview({tourRef, external = false}: TourPreviewProps) {
  const {data: tour, refetch} = useRefetchable(fragment, tourRef);
  const disabledControls = useSearchParam('disabledControls');
  const liveViewing = useSearchParam('liveViewing');
  const splitDisableControls = disabledControls?.toLowerCase()?.split(',');
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [buffer, setBuffer] = React.useState<FrameStoreBuffer>({
    totalImages: 0,
    storeLength: 0,
    fps: 0,
  });
  const drawerRef = React.useRef<HTMLDivElement>(null);
  const tourCanvasRef = React.useRef<HTMLCanvasElement>(null);
  const [drawerIsOpen, setDrawerIsOpen] = React.useState(false);
  const [fastTravellingStatus, setFastTravellingStatus] = useState<
    'idle' | 'forward' | 'backward'
  >('idle');

  const currentClipIndex = React.useMemo(() => {
    let frameSum = 0;
    const clips = tour.clips ?? [];
    for (let i = 0; i < clips.length; i++) {
      frameSum += clips[i].frameCount ?? 0;
      if (currentIndex < frameSum) return i;
    }
    return Math.max(0, clips.length - 1);
  }, [currentIndex, tour.clips]);

  const handleGetCanvasDimensions = React.useCallback(
    (width: number, height: number) => {
      if (
        width == null ||
        width === 0 ||
        height == null ||
        height === 0 ||
        liveViewing
      ) {
        return;
      }

      if (
        height > width &&
        window.innerHeight - height - parseInt(TOUR_DRAWER_HEIGHT) > 150
      ) {
        setDrawerIsOpen(true);
      } else if (
        !isMobile &&
        width > height &&
        window.innerWidth - width - parseInt(TOUR_DRAWER_WIDTH) > 450
      ) {
        setDrawerIsOpen(true);
      }
    },
    [isMobile, liveViewing]
  );

  return (
    <TourUIContextProvider>
      <Stack
        minHeight="100svh"
        maxHeight="100svh"
        overflow="hidden"
        justifyContent="space-between"
      >
        <div
          style={{
            overflow: 'hidden',
            position: 'absolute',
            inset: 0,
            left: 0,
            zIndex: 0,
            backgroundColor: '#758089',
            display: 'grid',
            placeContent: 'center',
          }}
        >
          {tour.thumbnailURL != null && (
            <img
              src={tour.thumbnailURL}
              alt={'thumbnail'}
              style={{
                width: '120vw',
                height: '120dvh',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                objectFit: 'cover',
                objectPosition: 'center',
                filter: 'blur(50px)',
                opacity: 0.9,
              }}
            />
          )}
        </div>
        <Stack position="relative">
          {!splitDisableControls?.includes('menu') && (
            <TourPreviewMenu
              drawerIsOpen={drawerIsOpen}
              title={tour.title}
              tourId={tour.id}
              external={external}
            />
          )}
        </Stack>
        <TourPlayer
          fastTravellingStatus={fastTravellingStatus}
          tourRef={tour}
          currentIndex={currentIndex}
          displayController={buffer.storeLength > 0}
          drawerIsOpen={drawerIsOpen}
          drawerRef={drawerRef}
          tourCanvasRef={tourCanvasRef}
          maxIndex={buffer.storeLength}
          setBuffer={setBuffer}
          setCurrentIndex={setCurrentIndex}
          getCanvasDimensions={handleGetCanvasDimensions}
        />
        <Stack alignItems={'center'} px={2}>
          {liveViewing?.toLowerCase() === 'true' && (
            <TourWebRTC
              drawerRef={drawerRef}
              drawerIsOpen={drawerIsOpen}
              tourId={tour.id}
              viewingSessionRequest={tour.viewingSessionRequest}
              isHost={!external}
            />
          )}
          {!splitDisableControls?.includes('markers') && (
            <TourPreviewDrawer
              setFastTravellingStatus={setFastTravellingStatus}
              drawerRef={drawerRef}
              drawerIsOpen={drawerIsOpen}
              keepDrawerOpen={false}
              setDrawerIsOpen={setDrawerIsOpen}
              tourRef={tour}
              buffer={buffer}
              currentIndex={currentIndex}
              setCurrentIndex={setCurrentIndex}
              liveViewing={liveViewing?.toLowerCase() === 'true'}
              tourId={tour.id}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              inspections={tour.inspections as any}
              currentClipIndex={currentClipIndex}
              refetch={() => refetch({}, {fetchPolicy: 'network-only'})}
            />
          )}
        </Stack>
      </Stack>
    </TourUIContextProvider>
  );
}
